import { useRef } from "react";
import { useInView } from "@/hooks/use-in-view";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqs: { q: string; a: React.ReactNode }[] = [
  { q: "Combien de temps durent les travaux de carrosserie ?", a: "La réparation prend 2 à 3 jours ouvrables une fois l'accord de l'assurance et les pièces reçus. Nous vous fournissons un planning précis lors de l'établissement du devis, et un véhicule de courtoisie est disponible dès CHF 49.– par jour pendant cette période." },
  { q: "Gérez-vous les démarches avec mon assurance ?", a: <>Absolument. Nous prenons en charge l'intégralité de la gestion administrative avec votre compagnie d'assurance. Après l'expertise de votre véhicule, nous établissons un devis détaillé accompagné de photos que nous transmettons directement à votre assureur. Vous n'avez qu'à nous fournir vos coordonnées d'assurance, et nous nous occupons du reste. Vous ne réglez que votre franchise. Le marché suisse de l'assurance est supervisé par la <a href="https://www.finma.ch/fr/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">FINMA</a>.</> },
  { q: "La teinte de peinture sera-t-elle exactement la même ?", a: "Oui, c'est notre garantie. Nous utilisons un spectrophotomètre de dernière génération qui analyse avec précision la couleur exacte de votre véhicule, en tenant compte de l'évolution naturelle de la teinte au fil du temps (exposition UV, vieillissement). Notre cabine de peinture pressurisée et nos techniques d'application professionnelles garantissent une correspondance colorimétrique parfaite, invisible à l'œil nu." },
  { q: "Quels sont vos délais d'intervention ?", a: "Nous établissons le devis sous 24 heures et l'envoyons à votre assurance. Une fois l'accord de l'assurance et les pièces reçus, la réparation prend 2 à 3 jours ouvrables. En cas d'urgence, contactez-nous directement." },
  { q: "Venez-vous chercher la voiture ?", a: "Oui, dans tout le canton de Genève, et on vous rend la voiture là où on l'a prise : domicile, bureau ou parking. Vous n'avez pas besoin de passer à l'atelier." },
  { q: "Réparez-vous les pare-brise ?", a: "Oui, nous réparons et remplaçons les pare-brise. Le bris de glace est souvent couvert par la casco partielle, selon votre contrat : nous faisons la déclaration et suivons le dossier avec votre assurance." },
  { q: "Travaillez-vous avec toutes les assurances ?", a: <>Oui, nous collaborons avec l'ensemble des compagnies d'assurance présentes en Suisse : AXA, Allianz, Zurich, Generali, Mobilière, Helvetia, Baloise, et toutes les autres. En tant qu'atelier indépendant, nous garantissons une liberté totale de choix, conformément aux réglementations de la <a href="https://www.finma.ch/fr/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">FINMA</a>. Quel que soit votre assureur, nous gérons les démarches de A à Z.</> },
  { q: "Proposez-vous un devis gratuit ?", a: "Oui, l'établissement du devis est entièrement gratuit et sans engagement. Il vous suffit de nous contacter par téléphone, WhatsApp, ou de passer directement à l'atelier avec votre véhicule. Nous effectuons une expertise complète, prenons les photos nécessaires, et vous remettons un devis détaillé sous 24 heures. Vous décidez ensuite en toute tranquillité." },
  { q: "Utilisez-vous des pièces d'origine constructeur ?", a: "Nous privilégions systématiquement les pièces d'origine constructeur pour garantir un ajustement parfait, une durabilité optimale et le maintien de la garantie de votre véhicule. Dans certains cas, et toujours avec votre accord, nous pouvons proposer des pièces alternatives de qualité équivalente qui permettent de réduire les coûts tout en assurant une qualité irréprochable." },
];

const FaqSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="faq" className="py-24 bg-muted/40 relative">

      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Questions fréquentes</h2>
        </div>

        <div className="max-w-3xl mx-auto mb-12 grid gap-4 sm:grid-cols-3">
          <div className="glass-card rounded-lg p-5">
            <h3 className="text-sm font-semibold mb-2">Comment réparez-vous une bosse ?</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Par redressage de la tôle puis peinture de l'élément, avec une teinte mesurée au spectrophotomètre. Comptez 2 à 3 jours ouvrables une fois l'accord et les pièces reçus.
            </p>
          </div>
          <div className="glass-card rounded-lg p-5">
            <h3 className="text-sm font-semibold mb-2">Combien coûte une réparation ?</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Le devis est gratuit chez ProImpact à Genève. En cas de sinistre couvert, vous ne payez que votre franchise, nous facturons directement votre assureur.
            </p>
          </div>
          <div className="glass-card rounded-lg p-5">
            <h3 className="text-sm font-semibold mb-2">Quelles assurances ?</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              ProImpact gère toutes les assurances suisses : AXA, Allianz, Zurich, Mobilière, Generali, Helvetia, Baloise et autres.
            </p>
          </div>
        </div>

        {/* Glossaire visible - citabilité IA */}
        <div className="max-w-3xl mx-auto mb-12 glass-card rounded-lg p-6">
          <h3 className="text-base font-semibold mb-4">Glossaire carrosserie</h3>
          <dl className="grid gap-3 sm:grid-cols-2 text-sm">
            <div>
              <dt className="font-semibold text-foreground">Tôlerie automobile</dt>
              <dd className="text-muted-foreground">Réparation et redressage des éléments métalliques de la carrosserie après un choc.</dd>
            </div>
            <div>
              <dt className="font-semibold text-foreground">Redressage</dt>
              <dd className="text-muted-foreground">Remise en forme de la tôle déformée par un choc, une bosse ou la grêle, avant la peinture de l'élément.</dd>
            </div>
            <div>
              <dt className="font-semibold text-foreground">Spectrophotomètre</dt>
              <dd className="text-muted-foreground">Appareil qui analyse la teinte exacte de votre véhicule pour une correspondance parfaite de la peinture.</dd>
            </div>
            <div>
              <dt className="font-semibold text-foreground">Franchise d'assurance</dt>
              <dd className="text-muted-foreground">Part des frais qui reste à votre charge en cas de sinistre couvert par votre assurance.</dd>
            </div>
            <div>
              <dt className="font-semibold text-foreground">Pièce OEM</dt>
              <dd className="text-muted-foreground">Pièce d'origine constructeur, identique à celle montée en usine, garantissant ajustement et durabilité.</dd>
            </div>
            <div>
              <dt className="font-semibold text-foreground">Bris de glace</dt>
              <dd className="text-muted-foreground">Dommage au pare-brise ou aux vitres, souvent couvert par la casco partielle selon le contrat.</dd>
            </div>
          </dl>
        </div>

        <div
          ref={ref}
          className={`max-w-3xl mx-auto reveal ${isInView ? "in-view" : ""}`}
          style={{ transitionDelay: "0.1s" }}
        >
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="glass-card rounded-lg px-6"
              >
                <AccordionTrigger className="text-left text-sm sm:text-base font-semibold hover:no-underline">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* FAQ texte plein pour crawlers et IA - invisible visuellement */}
        <div className="sr-only">
          {faqs.map((faq, i) => (
            <div key={`sr-${i}`}>
              <h3>{faq.q}</h3>
              <p>{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
